import { useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { PrivacyBanner } from '../components/PrivacyBanner';
import { fmtMoney } from '../utils/format';
import { uuid } from '../utils/uuid';
import type { Account } from '../types';

const KIND_OPTIONS: { value: Account['kind']; label: string; bucket: Account['bucket'] }[] = [
  { value: 'hysa', label: 'HYSA', bucket: 'hysa' },
  { value: 'checking', label: 'Checking', bucket: null },
  { value: 'taxable', label: 'Taxable brokerage', bucket: 'taxable' },
  { value: 'retirement', label: 'Retirement (401k/IRA)', bucket: 'retirement' },
  { value: 'home', label: 'Home', bucket: null },
  { value: 'mortgage', label: 'Mortgage', bucket: null },
  { value: 'other_asset', label: 'Other asset', bucket: 'taxable' },
  { value: 'other_liability', label: 'Other liability', bucket: null },
];

export function AccountsPage({ onBack }: { onBack: () => void }) {
  const { accounts, currentBalances, saveAccount, deleteAccount } = useStore();
  const [editing, setEditing] = useState<Account | null>(null);
  const [showNew, setShowNew] = useState(false);

  return (
    <>
      <TopBar
        title="Accounts"
        subtitle={`${accounts.length} active`}
        right={
          <>
            <button className="btn btn-ghost text-sm" onClick={onBack}>
              Done
            </button>
            <button className="btn btn-primary text-sm" onClick={() => setShowNew(true)}>
              + Add
            </button>
          </>
        }
      />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav">
        <PrivacyBanner />
        <section className="card card-dark">
          <ul>
            {accounts.map(a => (
              <li
                key={a.id}
                className="flex items-center gap-3 border-b border-ink-100 px-4 py-3 last:border-b-0 dark:border-ink-700"
              >
                <div className="flex-1">
                  <div className="text-sm font-semibold">{a.name}</div>
                  <div className="text-[11px] text-ink-500 dark:text-ink-300">
                    {KIND_OPTIONS.find(k => k.value === a.kind)?.label}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm tabular-nums">
                    {fmtMoney(currentBalances[a.id] ?? 0)}
                  </div>
                </div>
                <button
                  className="btn btn-ghost text-xs"
                  onClick={() => setEditing(a)}
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        </section>

        {(editing || showNew) && (
          <AccountEditor
            account={
              editing ??
              ({
                id: uuid(),
                name: '',
                kind: 'taxable',
                bucket: 'taxable',
                realReturnOverride: null,
                order: accounts.length,
                archived: false,
                createdAt: new Date().toISOString(),
              } as Account)
            }
            isNew={!editing}
            onCancel={() => {
              setEditing(null);
              setShowNew(false);
            }}
            onSave={async a => {
              await saveAccount(a);
              setEditing(null);
              setShowNew(false);
            }}
            onDelete={
              editing
                ? async () => {
                    if (confirm(`Delete ${editing.name}? Historical snapshot values for this account will remain but won't sum into totals.`)) {
                      await deleteAccount(editing.id);
                      setEditing(null);
                    }
                  }
                : undefined
            }
          />
        )}
      </main>
    </>
  );
}

function AccountEditor({
  account,
  isNew,
  onCancel,
  onSave,
  onDelete,
}: {
  account: Account;
  isNew: boolean;
  onCancel: () => void;
  onSave: (a: Account) => void;
  onDelete?: () => void;
}) {
  const [a, setA] = useState<Account>(account);

  return (
    <section className="card card-dark space-y-3 p-4">
      <h3 className="text-sm font-semibold">{isNew ? 'New account' : `Edit ${account.name}`}</h3>
      <div>
        <label className="label">Name</label>
        <input
          className="input mt-1"
          value={a.name}
          placeholder="e.g. Brokerage account"
          onChange={e => setA({ ...a, name: e.target.value })}
        />
      </div>
      <div>
        <label className="label">Type</label>
        <select
          className="input mt-1"
          value={a.kind}
          onChange={e => {
            const opt = KIND_OPTIONS.find(k => k.value === e.target.value)!;
            setA({ ...a, kind: opt.value, bucket: opt.bucket });
          }}
        >
          {KIND_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <button className="btn btn-ghost flex-1" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button className="btn btn-danger" onClick={onDelete}>
            Delete
          </button>
        )}
        <button
          className="btn btn-primary flex-1"
          onClick={() => onSave(a)}
          disabled={!a.name.trim()}
        >
          {isNew ? 'Create' : 'Save'}
        </button>
      </div>
    </section>
  );
}
